import { Link } from "react-router-dom";
import {
  Store,
  Rocket,
  Building2,
  ShieldCheck,
  HeartPulse,
  Landmark,
  FileCheck2,
  Briefcase,
  ArrowRight,
} from "lucide-react";
import { INDUSTRIES } from "@/data/site";

const ICONS = {
  Store,
  Rocket,
  Building2,
  ShieldCheck,
  HeartPulse,
  Landmark,
  FileCheck2,
  Briefcase,
};

const DETAIL = {
  "Small Businesses":
    "Right-sized network and security foundations — Wi-Fi, firewalls, segmentation, and reliable connectivity without enterprise overhead.",
  Startups:
    "Cloud and on-prem networking that grows with your team. We help avoid the rework that comes from short-cut early infrastructure decisions.",
  Enterprises:
    "Multi-site WAN, segmentation, Zero Trust alignment, and operational support across complex, distributed environments.",
  "Government Contractors":
    "Documentation-grade engineering, controlled change management, and audit-ready posture for sensitive workloads.",
  Healthcare:
    "Secure connectivity, isolation of clinical systems, and tight access control for protected data and devices.",
  Finance:
    "Hardened perimeters, rigorous access control, and defensive segmentation for transaction-critical environments.",
  "Compliance-Focused Orgs":
    "Best-practice alignment, gap analysis, and the documentation auditors actually look for.",
  "Public Sector Partners":
    "Mission-critical operations support, network modernization, and reliable change-controlled execution.",
};

export default function Industries() {
  return (
    <div data-testid="page-industries">
      <section className="relative border-b border-slate-800/80 bg-[#060B15] overflow-hidden">
        <div className="absolute inset-0 bek-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 bek-radial-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="bek-eyebrow mb-5">Industries</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-bold tracking-tight max-w-4xl">
            We serve a wide range of organizations —{" "}
            <span className="bek-chrome">on-site, remote, and hybrid.</span>
          </h1>
          <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
            From early-stage startups to regulated enterprises, our engagements share one thing in common:
            infrastructure that has to work — reliably and securely.
          </p>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {INDUSTRIES.map((ind, i) => {
              const Icon = ICONS[ind.icon];
              return (
                <div
                  key={ind.name}
                  data-testid={`industry-card-${ind.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="bg-[#0B1220] border border-slate-800 p-8 sm:p-10 bek-card-hover relative"
                >
                  <div className="absolute top-4 right-4 font-mono-tech text-xs text-slate-700">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="w-12 h-12 grid place-items-center border border-slate-300/40">
                      <Icon className="w-6 h-6 text-slate-300" />
                    </span>
                    <h2 className="text-2xl text-white font-semibold tracking-tight">{ind.name}</h2>
                  </div>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    {DETAIL[ind.name] || ind.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#060B15] border-y border-slate-800/80">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight">
            Not sure where you fit? <span className="bek-chrome">Let's talk.</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Most of our clients describe their environment as "complicated." We're comfortable with that.
          </p>
          <Link to="/contact" data-testid="industries-cta" className="bek-cta mt-8">
            Request Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
